<template>
  <div class="filter-pill">
    <PillLabel
      :icon-name="iconName"
      :label="label"
      :type="type"
    />
    <FilterVariation :type="type" />
    <v-popover
      ref="filterPopover"
      interactive
      tag="div"
      content-tag="div"
      :role="'poper'"
      :trigger="'click'"
      :on-show="openFilterPopup"
    >
      <template #default>
        <div
          v-if="showFilterValue"
          ref="filterButton"
          class="filter-pill-member-icon pill-segment usn pt"
          :class="{ 'no-chng': isBooleanFilter, 'mr-2': disableFilterRemoval }"
        >
          <PillAvatars
            v-if="showUserAvatars"
            :no-assignee="hasNoAssignees"
            :first-three-values="firstThreeValues"
            :group-avatar-id="groupAvatarId"
            :type="type"
          />
          <PillValueIcons
            v-else
            :type="type"
            :icon-name="iconName"
            :display-multiple-filter-icons="displayMultipleFilterIcons"
            :icons="firstThreeIcons"
          />
          <PillCountLabel
            v-if="!isTextFilter"
            :count-label="countLabel || ''"
            :has-no-assignees="hasNoAssignees"
            :show-user-avatars="showUserAvatars"
            :display-multiple-filter-icons="displayMultipleFilterIcons"
            :first-member="firstMember"
            :values-count="valuesCount"
          />
          <PillLabel
            v-if="isTextFilter"
            :label="filterValue"
            :type="type"
          />
        </div>
      </template>
      <template #content="{ state, hide }">
        <FilterPopup
          v-if="state.isVisible"
          :preselected-type="type"
          @hide-filter-popup="hide"
        />
      </template>
    </v-popover>

    <div
      v-if="!disableFilterRemoval"
      class="filter-pill-remove pill-segment pt usn"
      @click="removeFilter"
    >
      <em
        :style="getCustomIconStyle(iconPath('x'))"
        class="custom-icons w-3 h-3"
      />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import EventBus from '@/utilities/eventBus';
import getMemberLabel from '@/utilities/getMemberLabel';
import FilterPopup from './FilterPopup.vue';
import FilterVariation from './FilterVariation.vue';
import cardStatuses from '@/mixins/cardStatuses';
import cardRelationStatus from '@/mixins/cardRelationStatus';
import viewFiltersMixin from '@/mixins/viewFiltersMixin';
import estimateOptions from '@/mixins/estimateOptions';
import { nanoid } from 'nanoid';
import PillLabel from './PillLabel.vue';
import PillAvatars from './PillAvatars.vue';
import PillValueIcons from './PillValueIcons.vue';
import PillCountLabel from './PillCountLabel.vue';
import usePriorityOptions from '@/utilities/composables/usePriorityOptions';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import { ResourceType } from '@/types/resources';
import cardStatusType from '@/types/cardStatusType';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { useMyWorkMemberRoute } from '@/utilities/composables/useMyWorkMemberRoute';

export default {
  name: 'FilterPill',
  components: {
    FilterPopup,
    FilterVariation,
    PillLabel,
    PillAvatars,
    PillValueIcons,
    PillCountLabel,
  },
  mixins: [cardStatuses, cardRelationStatus, viewFiltersMixin, estimateOptions],

  props: {
    type: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { priorityOptions } = usePriorityOptions();
    const { embeddedBoardAttributes, embed } = useBoardEmbed();
    const { getCustomIconStyle, iconPath } = useCustomIcon();
    const { isMyWorkOrMemberRoute, isMyWorkAssignedTab, isMyWorkCreatedTab } =
      useMyWorkMemberRoute();

    const disableFilterRemoval = computed(() => {
      return (
        isMyWorkOrMemberRoute.value &&
        ((isMyWorkAssignedTab.value && props.type === 'members') ||
          (isMyWorkCreatedTab.value && props.type === 'creators'))
      );
    });

    return {
      priorityOptions,
      embeddedBoardAttributes,
      embed,
      getCustomIconStyle,
      iconPath,
      isMyWorkOrMemberRoute,
      disableFilterRemoval,
    };
  },

  data() {
    return {
      eventRegTimeout: null,
      groupAvatarId: nanoid(6),
    };
  },

  computed: {
    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.boardId;
      }
      return this.$store.getters.currentBoardId;
    },

    currentFilters() {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        return this.$store.getters.getViewFilters;
      }

      return this.$store.getters.boardFilters(this.currentBoardId) || {};
    },

    label() {
      switch (this.type) {
        case 'members':
          return this.translate('assignee');
        case 'owners':
          return this.translate('owner');
        case 'creators':
          return this.$capitalizeFirstLetter(this.translate('creator'));
        case 'tags':
          return this.$capitalizeFirstLetter(this.translate('tag'));
        case 'statuses':
          return this.$capitalizeFirstLetter(this.translate('status'));
        case 'boards':
          return this.$capitalizeFirstLetter(this.translate('board'));
        case 'projects':
          return this.$capitalizeFirstLetter(this.translate('space'));
        case 'linked_cards':
          return this.$capitalizeFirstLetter(this.translate('relationship'));
        case 'is_parent':
        case 'is_child':
          return this.translate('card');
        case 'due_date':
          return this.translate('dueDate');
        case 'time_created':
          return this.translate('created');
        case 'time_updated':
          return this.translate('updated');
        case 'external_links':
          return this.translate('links');
        case 'priority':
          return this.translate(this.type);
        case 'estimates':
          return this.translate('estimate');
        case 'parent_cards':
          return this.translate('parentCard');
        case 'epics':
          return this.translate('epic');
        case 'title':
          return this.translate(this.type);
        default:
          return this.type;
      }
    },

    iconName() {
      switch (this.type) {
        case 'members':
          return 'user-check';
        case 'owners':
        case 'creators':
          return 'user';
        case 'tags':
          return 'tag';
        case 'statuses':
          return 'circle';
        case 'boards':
          return 'board';
        case 'projects':
          return 'folder';
        case 'linked_cards':
          return 'flag';
        case 'is_parent':
          return 'parent-card';
        case 'is_child':
          return 'card-child';
        case 'time_updated':
        case 'due_date':
        case 'time_created':
          return 'calendar';
        case 'external_links':
          return 'link-st';
        case 'priority':
          return 'chevrons-up';
        case 'estimates':
          return this.estimateDefaultIcon.src;
        case 'parent_cards':
          return 'parent-card';
        case 'epics':
          return this.$constants.epicDefaultIcon;
        case 'title':
          return 'type';
        default:
          return '';
      }
    },

    showFilterValue() {
      return this.$constants.filterTypes.includes(this.type);
    },

    hasNoAssignees() {
      return this.type === 'members' && this.currentFilters.has_members !== undefined;
    },

    isBooleanFilter() {
      return ['is_parent', 'is_child'].includes(this.type);
    },

    isDateFilter() {
      return ['has_due_date', 'due_date', 'time_created', 'time_updated'].includes(this.type);
    },

    showUserAvatars() {
      return ['members', 'creators', 'owners'].includes(this.type);
    },

    firstThreeValues() {
      if (this.currentFilters.include[this.type]?.length) {
        if (this.currentFilters.has_members === false && this.type === 'members') {
          return this.currentFilters.include[this.type].slice(0, 2);
        }

        return this.currentFilters.include[this.type].slice(0, 3);
      }
      if (this.currentFilters.exclude[this.type]?.length) {
        if (this.currentFilters.has_members === true && this.type === 'members') {
          return this.currentFilters.exclude[this.type].slice(0, 2);
        }

        return this.currentFilters.exclude[this.type].slice(0, 3);
      }
      return [];
    },

    firstOne() {
      if (this.isDateFilter) {
        if (!this.currentFilters?.has_due_date && !this.currentFilters?.[this.type]) {
          return '0';
        }
        return this.currentFilters?.[this.type].before || this.currentFilters?.[this.type].after;
      }

      if (this.isBooleanFilter) {
        return '';
      }

      if (
        this.hasNoAssignees &&
        !Object.keys(this.currentFilters.include?.[this.type] || {}).length &&
        !Object.keys(this.currentFilters.exclude?.[this.type] || {}).length
      ) {
        return 'no_assignee';
      }

      if (
        this.type === 'statuses' &&
        !Object.keys(this.currentFilters.include?.[this.type] || {}).length &&
        !Object.keys(this.currentFilters.exclude?.[this.type] || {}).length
      ) {
        return 'backlog';
      }

      if (['priority', 'estimates'].includes(this.type)) {
        return (
          this.currentFilters?.include?.[this.type]?.[0] ||
          this.currentFilters?.exclude?.[this.type]?.[0] ||
          0
        );
      }

      return (
        this.currentFilters?.include?.[this.type]?.[0] ||
        this.currentFilters?.exclude?.[this.type]?.[0] ||
        ''
      );
    },

    firstMember() {
      const memberId = this.firstThreeValues[0] || '';
      return this.$store.getters.getMemberById(memberId) || {};
    },

    countLabel() {
      if (this.valuesCount === 1) {
        if (this.showUserAvatars) {
          if (this.hasNoAssignees) return this.translate('no_assignee');

          return getMemberLabel(this.firstMember);
        }
        if (this.type === 'tags') {
          const tag = this.$store.getters.getTags[this.firstOne];
          return tag?.name;
        }

        if (this.type === 'priority') {
          const priority = this.priorityOptions.find((item) => item.id === this.firstOne);
          return this.translate(priority?.label);
        }

        if (this.type === 'estimates') {
          return this.firstOne === 0
            ? this.translate('noEstimate')
            : this.generateEstimateLabelForFilters(parseInt(this.firstOne) - 1);
        }

        if (this.type === 'statuses') {
          return `${this.statusLabel}`;
        }

        if (this.type === 'linked_cards') {
          return `${this.relationshipLabel}`;
        }

        if (this.type === 'boards') {
          return `${this.boardTitle}`;
        }

        if (this.type === 'projects') {
          return `${this.projectTitle}`;
        }

        if (this.type === 'external_links') {
          return this.translate(this.firstOne);
        }

        if (this.type === 'parent_cards') {
          return `${this.parentCard?.title || ''}`;
        }

        if (this.type === 'epics') {
          return `${this.epic?.title || ''}`;
        }
      }

      if (this.isDateFilter) {
        if (this.firstOne === '0') {
          return `${this.translate('noduedate')}`;
        }
        if (this.firstOne === 'now') {
          return `${this.translate('overdue')}`;
        }
        return `${this.translate(this.firstOne)}`;
      }

      let label = {
        creators: 'creators',
        owners: 'owners',
        members: 'assignees',
        is_parent: 'parent',
        is_child: 'child',
        linked_cards: 'types',
        tags: 'tags',
        statuses: 'status',
        boards: 'boards',
        projects: 'spaces',
        priority: 'priorities',
        estimates: 'estimates',
        parent_cards: 'cards',
        epics: 'projects',
      }[this.type];

      label = this.isBooleanFilter
        ? this.$capitalizeFirstLetter(this.translate(label))
        : this.translate(label).toLowerCase();

      return `${this.valuesCount === 0 ? '' : this.valuesCount + ' '}${label}`;
    },

    backlogCountIn() {
      return this.type === 'statuses' && this.currentFilters.has_status !== undefined ? 1 : 0;
    },

    noAssigneesCountIn() {
      return this.hasNoAssignees ? 1 : 0;
    },

    isTextFilter() {
      return this.type === 'title';
    },

    filterValue() {
      if (this.isTextFilter) {
        if (this.currentFilters.include?.[this.type]) {
          return this.currentFilters.include?.[this.type];
        }
        if (this.currentFilters.exclude?.[this.type]) {
          return this.currentFilters.exclude?.[this.type];
        }
      }
      return '';
    },

    valuesCount() {
      if (this.isBooleanFilter) {
        return 0;
      }

      if (this.currentFilters.include[this.type]?.length) {
        return (
          this.currentFilters.include[this.type]?.length +
          this.backlogCountIn +
          this.noAssigneesCountIn
        );
      }
      if (this.currentFilters.exclude[this.type]?.length) {
        return (
          this.currentFilters.exclude[this.type]?.length +
          this.backlogCountIn +
          this.noAssigneesCountIn
        );
      }

      return this.backlogCountIn || this.noAssigneesCountIn;
    },

    displayMultipleFilterIcons() {
      return [
        'tags',
        'projects',
        'statuses',
        'linked_cards',
        'priority',
        'estimates',
        'parent_cards',
        'epics',
      ].includes(this.type);
    },

    displayIcon() {
      if (!this.displayMultipleFilterIcons) {
        return true;
      }

      return this.valuesCount === 1;
    },

    displayValueIcon() {
      return (
        this.type !== 'is_parent' &&
        this.type !== 'is_child' &&
        this.type !== 'due_date' &&
        this.type !== 'time_created' &&
        this.type !== 'boards'
      );
    },

    multivalueFilter() {
      return [
        'owners',
        'creators',
        'members',
        'tags',
        'external_links',
        'priority',
        'estimates',
        'parent_cards',
        'epics',
      ].includes(this.type);
    },

    statusLabel() {
      return this.cardStatuses[this.firstOne]?.label || '';
    },

    relationshipLabel() {
      const relationStatus = this.cardRelationStatus[this.firstOne];
      return relationStatus ? this.translate(relationStatus.label) : '';
    },

    boardTitle() {
      return this.$store.getters.getFlatBoards[this.firstOne]?.title || '';
    },

    project() {
      return this.$store.getters.getTeamProjectById(this.firstOne) || {};
    },

    projectTitle() {
      return this.project?.title || '';
    },

    firstThreeIcons() {
      if (!this.displayMultipleFilterIcons) {
        return [];
      }

      const firstThreeIcons = [];
      let src, color;

      if (this.type === 'projects') {
        this.firstThreeValues.forEach((val) => {
          const proj = this.$store.getters.getTeamProjectById(val) || {};
          src = proj.icon?.src || '';
          color = proj.icon?.color || '';
          const type = proj.icon?.type || '';
          firstThreeIcons.push({ src, color, type });
        });
      }

      if (this.type === 'statuses') {
        if (this.currentFilters.has_status !== undefined) {
          firstThreeIcons.push({
            src: this.cardStatuses.backlog.icon,
            color: this.cardStatuses.backlog.color,
          });
        }

        const isEpic = this.currentBoardId === this.$constants.epicsBoardId;
        this.firstThreeValues.forEach((val) => {
          src = getCardStatus(val, isEpic).icon;
          ({ color } = getCardStatus(val, isEpic));

          firstThreeIcons.push({ src, color });
        });
      }

      if (this.type === 'priority') {
        this.firstThreeValues.forEach((val) => {
          firstThreeIcons.push({
            src: this.priorityOptions.find((item) => item.id === val)?.icon,
            color: this.priorityOptions.find((item) => item.id === val).customIconColor,
          });
        });
      }

      if (this.type === 'tags') {
        return this.firstThreeValues;
      }

      if (this.type === 'linked_cards') {
        this.firstThreeValues.forEach((val) => {
          src = this.cardRelationStatus[val].icon;
          ({ color } = this.cardRelationStatus[val]);

          firstThreeIcons.push({ src, color });
        });
      }

      if (this.type === 'parent_cards') {
        if (this.valuesCount > 1) {
          return [];
        } else {
          firstThreeIcons.push({
            src:
              this.cardStatuses[this.parentCard.status]?.icon ||
              getCardStatus(cardStatusType.Backlog).icon,
            color:
              this.cardStatuses[this.parentCard.status]?.color ||
              getCardStatus(cardStatusType.Backlog).color,
          });
        }
        return firstThreeIcons.slice(0, 1);
      }

      if (this.type === 'epics') {
        this.firstThreeValues.forEach((val) => {
          const card = this.$store.getters.getCardById(val) || {};
          firstThreeIcons.push({
            src: card?.icon?.src || this.$constants.epicDefaultIcon,
            color: card?.icon?.color,
            type: card?.icon?.type || 'icon',
          });
        });
      }

      return firstThreeIcons.slice(0, 3);
    },

    cards() {
      return this.$store.getters.getCards;
    },

    parentCard() {
      const card = this.cards[this.firstOne];

      if (!card) {
        const boardCards = this.$store.getters.getBoardsCardsIdsByBoardId(this.currentBoardId);
        for (const cardId of boardCards) {
          const boardCard = this.$store.getters.getCardById(cardId);
          if (boardCard?.parent_card?.card_id === this.firstOne) {
            return boardCard.parent_card;
          }
        }
      }

      return card || {};
    },

    epic() {
      const selectedEpicIds = [];
      if (this.currentFilters.include?.epics?.length) {
        this.currentFilters.include.epics.forEach((epicId) => {
          selectedEpicIds.push(epicId);
        });
      }
      if (this.currentFilters.exclude?.epics?.length) {
        this.currentFilters.exclude.epics.forEach((epicId) => {
          selectedEpicIds.push(epicId);
        });
      }

      const card = this.cards[this.firstOne];

      if (!card) {
        const boardCards = this.$store.getters.getBoardsCardsIdsByBoardId(this.currentBoardId);
        for (const cardId of boardCards) {
          const boardCard = this.$store.getters.getCardById(cardId);
          if (selectedEpicIds.includes(boardCard.epic?.id)) {
            return boardCard.epic;
          }
        }
      }

      return card || {};
    },
  },

  mounted() {
    if (this.type === 'epics') {
      this.fetchEpics();
    }
  },

  beforeUnmount() {
    EventBus.$off('manualHidePopup', this.hideFilterPopup);
    clearTimeout(this.eventRegTimeout);
  },

  methods: {
    fetchEpics() {
      const includeEpics = this.currentFilters.include?.epics || [];
      const excludeEpics = this.currentFilters.exclude?.epics || [];
      const epics = [...includeEpics, ...excludeEpics];

      epics.forEach((epicId) => {
        const epic = this.cards[epicId];
        if (!epic || epic.partially_loaded) {
          this.$store.dispatch('fetchCard', { cardId: epicId, type: ResourceType.Epic });
        }
      });
    },

    removeFilter() {
      const filters = { ...this.currentFilters };
      if (!this.isBooleanFilter) {
        delete filters.include[this.type];
        delete filters.exclude[this.type];

        if (this.type === 'statuses') {
          delete filters.has_status;
        }
        if (this.hasNoAssignees) {
          delete filters.has_members;
        }
      }
      if (this.isBooleanFilter || this.isDateFilter) {
        delete filters[this.type];
        delete filters?.has_due_date;
      }

      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        this.setViewFilters(filters);
        return;
      }

      this.$store.dispatch('setBoardFilters', { boardId: this.currentBoardId, filters });
    },

    hideFilterPopup() {
      this.$refs.filterPopover?.hide?.();

      EventBus.$off('manualHidePopup', this.hideFilterPopup);
    },

    openFilterPopup() {
      if (!this.$refs.filterButton || this.isBooleanFilter) return false;

      this.eventRegTimeout = setTimeout(() => {
        EventBus.$on('manualHidePopup', this.hideFilterPopup);
      }, 0);
      return true;
    },
  },
};
</script>

<style lang="scss">
.board-filter-pills {
  .filter-pill {
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid $gray300;
    border-radius: 16px;
    margin-right: 8px;
    margin-top: 6px;
    margin-bottom: 6px;
    padding: 0 0 0 4px;
    cursor: default;
    @apply bg-important-bg border border-important-border text-text-default;
  }
}
.filter-pill {
  .pill-segment {
    display: flex;
    min-width: 0;
    height: 100%;
    align-items: center;
    padding: 0 4px;

    > em,
    .filter-value-icon > em {
      height: $pillIconSize;
      width: $pillIconSize;
    }

    > em + span {
      margin-left: 4px;
    }
  }

  .pill-label,
  .filter-variation {
    font-weight: 500;
    font-size: 12px;
    line-height: 22px;
    color: $stPrimaryColor;
    @apply max-w-[150px]
    whitespace-nowrap
    text-ellipsis
    overflow-hidden
    text-text-default;
  }

  .filter-pill-remove {
    display: flex;
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;

    em.custom-icons {
      padding-right: 4px;
    }
  }

  .filter-pill-member-icon {
    display: flex;
  }

  .filter-variation,
  .filter-pill-member-icon,
  .filter-pill-remove {
    &:not(.no-chng) {
      &:hover {
        @apply bg-important-hover;

        .filter-value-icon {
          @apply bg-important-hover;
        }
      }
    }
  }
}
</style>
